import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import GridLayout, { type Layout } from "react-grid-layout";
import type { ChartCard as ChartCardModel } from "../store/workspace.js";
import type {
  GridItem,
  NewGridItem,
  Theme,
} from "../store/workspace.js";
import {
  GRID_COLS,
  MIN_CARD_H,
  MIN_CARD_W,
  rectanglesOverlap,
} from "../store/workspace.js";
import { ChartCard } from "./ChartCard.js";
import { GridPlacementLayer } from "./GridPlacementLayer.js";

const ROW_HEIGHT = 40;
const MARGIN: [number, number] = [16, 16];

interface ChartGridProps {
  cards: ChartCardModel[];
  layout: GridItem[];
  theme: Theme;
  onLayoutChange: (layout: GridItem[]) => void;
  onRemove: (id: string) => void;
  onUpdate: (id: string, changes: Partial<Omit<ChartCardModel, "id">>) => void;
  onPlace?: (item: NewGridItem) => void;
}

export function ChartGrid({
  cards,
  layout,
  theme,
  onLayoutChange,
  onRemove,
  onUpdate,
  onPlace,
}: ChartGridProps) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const restoreLayout = useRef(new Map<string, GridItem>());
  const shellRef = useRef<HTMLElement | null>(null);
  const interactionCleanupRef = useRef<(() => void) | null>(null);
  const [gridWidth, setGridWidth] = useState(720);

  useEffect(() => {
    const element = shellRef.current;
    if (!element) return undefined;

    const updateWidth = () => {
      setGridWidth(Math.max(1, Math.round(element.clientWidth)));
    };
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(
    () => () => interactionCleanupRef.current?.(),
    [],
  );

  const gridLayout: Layout[] = useMemo(
    () =>
      cards.map((card) => {
        const item = layout.find((l) => l.i === card.id);
        return {
          i: card.id,
          x: item?.x ?? 0,
          y: item?.y ?? Infinity,
          w: item?.w ?? 6,
          h: item?.h ?? 8,
          minW: 3,
          minH: 5,
        };
      }),
    [cards, layout],
  );

  const handleToggleExpand = useCallback(
    (id: string) => {
      const item = layout.find((candidate) => candidate.i === id);
      if (!item) return;

      if (expandedIds.has(id)) {
        const restore = restoreLayout.current.get(id);
        if (restore) {
          onLayoutChange(
            layout.map((candidate) => (candidate.i === id ? restore : candidate)),
          );
        }
        restoreLayout.current.delete(id);
        setExpandedIds((current) => {
          const next = new Set(current);
          next.delete(id);
          return next;
        });
        return;
      }

      restoreLayout.current.set(id, item);
      setExpandedIds((current) => new Set(current).add(id));
      onLayoutChange(
        layout.map((candidate) =>
          candidate.i === id
            ? {
                ...candidate,
                x: 0,
                w: GRID_COLS,
                h: Math.max(candidate.h, 9),
              }
            : candidate,
        ),
      );
    },
    [expandedIds, layout, onLayoutChange],
  );

  const gridRows = useMemo(() => {
    const bottom = layout.reduce(
      (maximum, item) => Math.max(maximum, item.y + item.h),
      0,
    );
    return Math.max(12, bottom + 8);
  }, [layout]);

  const persistLayout = useCallback(
    (next: Layout[]) => {
      onLayoutChange(
        next.map((item) => ({
          i: item.i,
          x: item.x,
          y: item.y,
          w: item.w,
          h: item.h,
        })),
      );
    },
    [onLayoutChange],
  );

  const startGridInteraction = useCallback(
    (
      mode: "move" | "resize",
      id: string,
      event: ReactPointerEvent<HTMLElement>,
    ) => {
      if (event.button !== 0) return;
      if (
        mode === "move" &&
        (event.target as HTMLElement).closest("button")
      ) {
        return;
      }

      const current = layout.find((item) => item.i === id);
      const grid = shellRef.current?.querySelector<HTMLElement>(".chart-grid");
      if (!current || !grid) return;

      event.preventDefault();
      event.stopPropagation();
      interactionCleanupRef.current?.();

      const gridRect = grid.getBoundingClientRect();
      const scale = gridRect.width / Math.max(1, grid.offsetWidth);
      const horizontalGap = MARGIN[0] * scale;
      const columnWidth =
        (gridRect.width - horizontalGap * (GRID_COLS - 1)) / GRID_COLS;
      const columnStep = columnWidth + horizontalGap;
      const rowStep = (ROW_HEIGHT + MARGIN[1]) * scale;
      const startX = event.clientX;
      const startY = event.clientY;
      const className =
        mode === "move" ? "is-moving-chart" : "is-resizing-chart";
      document.body.classList.add(className);

      const handlePointerMove = (moveEvent: PointerEvent) => {
        const deltaColumns = Math.round(
          (moveEvent.clientX - startX) / columnStep,
        );
        const deltaRows = Math.round((moveEvent.clientY - startY) / rowStep);
        const candidate: GridItem =
          mode === "move"
            ? {
                ...current,
                x: Math.max(
                  0,
                  Math.min(GRID_COLS - current.w, current.x + deltaColumns),
                ),
                y: Math.max(0, current.y + deltaRows),
              }
            : {
                ...current,
                w: Math.max(
                  MIN_CARD_W,
                  Math.min(
                    GRID_COLS - current.x,
                    current.w + deltaColumns,
                  ),
                ),
                h: Math.max(MIN_CARD_H, current.h + deltaRows),
              };

        const blocked = layout.some(
          (item) =>
            item.i !== id && rectanglesOverlap(candidate, item),
        );
        if (blocked) return;

        onLayoutChange(
          layout.map((item) => (item.i === id ? candidate : item)),
        );
      };

      const cleanup = () => {
        document.removeEventListener("pointermove", handlePointerMove);
        document.removeEventListener("pointerup", cleanup);
        document.removeEventListener("pointercancel", cleanup);
        document.body.classList.remove(className);
        interactionCleanupRef.current = null;
      };
      interactionCleanupRef.current = cleanup;
      document.addEventListener("pointermove", handlePointerMove);
      document.addEventListener("pointerup", cleanup);
      document.addEventListener("pointercancel", cleanup);
    },
    [layout, onLayoutChange],
  );

  return (
    <section
      ref={shellRef}
      className="chart-grid-shell"
      style={{
        minHeight: gridRows * (ROW_HEIGHT + MARGIN[1]) - MARGIN[1],
      }}
      aria-label="Chart dashboard"
    >
      <GridLayout
        className="chart-grid"
        width={gridWidth}
        layout={gridLayout}
        cols={GRID_COLS}
        rowHeight={ROW_HEIGHT}
        margin={MARGIN}
        containerPadding={[0, 0]}
        compactType={null}
        onLayoutChange={persistLayout}
        isDraggable={false}
        isResizable={false}
      >
        {cards.map((card) => (
          <div key={card.id} className="chart-grid__item">
            <ChartCard
              card={card}
              theme={theme}
              onRemove={onRemove}
              onUpdate={onUpdate}
              onMoveStart={(event) =>
                startGridInteraction("move", card.id, event)
              }
              onToggleExpand={() => handleToggleExpand(card.id)}
              expanded={expandedIds.has(card.id)}
            />
            <span
              className="chart-manual-resize-handle"
              role="separator"
              aria-label={`Resize ${card.symbol} chart`}
              title="Drag to resize"
              onPointerDown={(event) =>
                startGridInteraction("resize", card.id, event)
              }
            />
          </div>
        ))}
      </GridLayout>

      {onPlace && (
        <GridPlacementLayer
          layout={layout}
          rows={gridRows}
          onSelect={onPlace}
        />
      )}
    </section>
  );
}
